import { Box2, Vector2, Vector3 } from 'three';

import { newVector3 } from './newVector3';
import { Point3D } from './types';

const normalizeSurfaceToUvSpace = (vertArray: Point3D[], grainDirection: Vector3) => {
  const pointA = newVector3(vertArray[0]);
  const pointB = newVector3(vertArray[1]);
  const pointC = newVector3(vertArray[2]);
  const surfaceDirection = new Vector3()
    .copy(pointB)
    .sub(pointA)
    .cross(new Vector3().copy(pointC.sub(pointA)));
  surfaceDirection.normalize();

  // Ideally grainDirection should be coplanar with the surface. If it is not, we will force it to be
  // (somewhat arbitrarily).
  let effectiveGrainDirection: Vector3;
  const parallelComponent = grainDirection.dot(surfaceDirection);
  if (Math.abs(parallelComponent) < 0.8) {
    // If grainDirection is not much more than 45 degrees from the surface, project it onto the surface.
    effectiveGrainDirection = new Vector3()
      .copy(grainDirection)
      .sub(new Vector3().copy(surfaceDirection).multiplyScalar(parallelComponent));
  } else if (Math.abs(surfaceDirection.dot(new Vector3(0, 0, 1))) < 0.8) {
    // Otherwise, if the surface is not much less than 45 degrees from the xy plane, choose a grain direction in that
    // plane.
    effectiveGrainDirection = new Vector3(0, 0, 1).cross(surfaceDirection);
  } else {
    // Otherwise, the surface is more than 45 degrees from the yz plane. Choose a grain direction in that plane.
    effectiveGrainDirection = new Vector3(1, 0, 0).cross(surfaceDirection);
  }
  effectiveGrainDirection.normalize();

  const grainPerpendicular = new Vector3().copy(surfaceDirection).cross(effectiveGrainDirection);
  const result = vertArray.map(
    (vert) => new Vector2(effectiveGrainDirection.dot(newVector3(vert)), grainPerpendicular.dot(newVector3(vert))),
  );
  const boundBox = new Box2();
  boundBox.setFromPoints(result);
  result.forEach((vector: Vector2) => vector.sub(boundBox.min));
  return result;
};

export const unwrapMember = (
  vertices: readonly Point3D[],
  faces: ReadonlyArray<Readonly<[number, number, number]>>,
  surfaces: ReadonlyArray<ReadonlyArray<number>>,
  grainDirection: Vector3,
) => {
  const results = [];

  for (let i = 0; i < surfaces.length; i += 1) {
    const surfaceVertexIndices = surfaces[i].flatMap((index) => [faces[index][0], faces[index][1], faces[index][2]]);
    const surfaceVertices = surfaceVertexIndices.map((index) => vertices[index]);
    const normalizedVertices = normalizeSurfaceToUvSpace(surfaceVertices, grainDirection);

    for (let j = 0; j < normalizedVertices.length; j += 1) {
      const vertexIndex = surfaceVertexIndices[j];
      const normalizedVertex = normalizedVertices[j];
      if (
        results[vertexIndex] != null &&
        (normalizedVertex.x !== results[vertexIndex].x || normalizedVertex.y !== results[vertexIndex].y)
      ) {
        console.error('Trying to set the UV at a vertex index that has already been set.');
      }
      results[surfaceVertexIndices[j]] = new Vector2(normalizedVertices[j].x, normalizedVertices[j].y);
    }
  }
  return results;
};
